import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { products } from "../data/products";
import { useCart } from "../context/CartContext";
import Navbar from "../components/Navbar";

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const product = products.find((p) => p.id === parseInt(id));
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const imgRef = useRef(null);
  const detailRef = useRef(null);
  const backRef = useRef(null);
  const relatedRef = useRef(null);

  useEffect(() => {
    if (!product) return;

    const ctx = gsap.context(() => {
      // Back link slides down
      gsap.fromTo(
        backRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out", delay: 0.1 },
      );

      // Image slides in from left + fades
      gsap.fromTo(
        imgRef.current,
        { opacity: 0, x: -60, scale: 0.96 },
        {
          opacity: 1,
          x: 0,
          scale: 1,
          duration: 0.9,
          ease: "power3.out",
          delay: 0.2,
        },
      );

      // Details stagger in from right
      const children = detailRef.current?.children;
      if (children) {
        gsap.fromTo(
          children,
          { opacity: 0, x: 40 },
          {
            opacity: 1,
            x: 0,
            duration: 0.7,
            ease: "power2.out",
            stagger: 0.08,
            delay: 0.3,
          },
        );
      }

      // Related cards fade up
      if (relatedRef.current) {
        gsap.fromTo(
          relatedRef.current.children,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
            stagger: 0.1,
            delay: 0.5,
          },
        );
      }
    });

    return () => ctx.revert();
  }, [product]);

  if (!product) {
    return (
      <Page>
        <Navbar />
        <NotFound>
          <span>404</span>
          <p>Produkti nuk u gjet.</p>
          <BackBtn onClick={() => navigate("/")}>← Kthehu</BackBtn>
        </NotFound>
      </Page>
    );
  }

  const related = products
    .filter((p) => p.category === product.category && p.id !== product.id)
    .slice(0, 3);

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <Page>
      <Navbar />

      <Hero style={{ background: `${product.color}10` }}>
        <HeroInner>
          <BackLink ref={backRef} onClick={() => navigate(-1)}>
            ← Kthehu
          </BackLink>

          <HeroGrid>
            {/* Left: product image */}
            <ProductVisual ref={imgRef}>
              <VisualGlow style={{ background: `${product.color}30` }} />
              <ProductHeroImg src={product.image} alt={product.name} />
              {product.year && <YearTag>{product.year}</YearTag>}
            </ProductVisual>

            {/* Right: details */}
            <ProductDetails ref={detailRef}>
              <TypeBadge>{product.type}</TypeBadge>
              <ProductTitle>{product.name}</ProductTitle>
              <BrandRow>
                <BrandName>{product.brand}</BrandName>
                {product.origin && <OriginTag>📍 {product.origin}</OriginTag>}
              </BrandRow>

              <RatingRow>
                <Stars>
                  {"★".repeat(Math.floor(product.rating))}
                  {"☆".repeat(5 - Math.floor(product.rating))}
                </Stars>
                <RatingNum>{product.rating}</RatingNum>
                <ReviewText>({product.reviews} vlerësime)</ReviewText>
              </RatingRow>

              <Description>{product.description}</Description>

              <SpecsGrid>
                {product.alcohol && (
                  <Spec>
                    <SpecLabel>Alkool</SpecLabel>
                    <SpecVal>{product.alcohol}</SpecVal>
                  </Spec>
                )}
                <Spec>
                  <SpecLabel>Vëllim</SpecLabel>
                  <SpecVal>{product.volume}</SpecVal>
                </Spec>
                <Spec>
                  <SpecLabel>Servohet</SpecLabel>
                  <SpecVal>{product.serve}</SpecVal>
                </Spec>
              </SpecsGrid>

              {product.notes && (
                <NotesBlock>
                  <NotesTitle>Shënimet e Shijës</NotesTitle>
                  <NotesList>
                    {product.notes.map((n) => (
                      <NoteTag key={n}>{n}</NoteTag>
                    ))}
                  </NotesList>
                </NotesBlock>
              )}

              {product.food && (
                <NotesBlock>
                  <NotesTitle>Kombinime Ushqimore</NotesTitle>
                  <NotesList>
                    {product.food.map((f) => (
                      <FoodTag key={f}>{f}</FoodTag>
                    ))}
                  </NotesList>
                </NotesBlock>
              )}

              <PurchaseRow>
                <PriceBlock>
                  <BigPrice>€{product.price.toFixed(2)}</BigPrice>
                  <PriceSub>/ njësi</PriceSub>
                </PriceBlock>

                <QtyBlock>
                  <QtyBtn onClick={() => setQty(Math.max(1, qty - 1))}>
                    −
                  </QtyBtn>
                  <QtyNum>{qty}</QtyNum>
                  <QtyBtn onClick={() => setQty(qty + 1)}>+</QtyBtn>
                </QtyBlock>

                <AddToCartBtn onClick={handleAdd} $added={added}>
                  {added ? "✓ Shtuar!" : "Shto në Shportë"}
                </AddToCartBtn>
                <BuyNowBtn
                  onClick={() => {
                    handleAdd();
                    navigate("/checkout");
                  }}
                >
                  Bli Tani →
                </BuyNowBtn>
              </PurchaseRow>
            </ProductDetails>
          </HeroGrid>
        </HeroInner>
      </Hero>

      {/* Related products */}
      {related.length > 0 && (
        <RelatedSection>
          <RelatedInner>
            <RelatedTitle>Produkte të Ngjashme</RelatedTitle>
            <RelatedGrid ref={relatedRef}>
              {related.map((r) => (
                <RelatedCard
                  key={r.id}
                  onClick={() => navigate(`/product/${r.id}`)}
                >
                  <RelatedImg src={r.image} alt={r.name} />
                  <RelatedBody>
                    <RelatedName>{r.name}</RelatedName>
                    <RelatedPrice>€{r.price.toFixed(2)}</RelatedPrice>
                  </RelatedBody>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedInner>
        </RelatedSection>
      )}
    </Page>
  );
};

export default ProductPage;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50%       { opacity: 0.6; transform: scale(1.1); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  background: var(--bg);
  min-height: 100vh;
`;

const NotFound = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  gap: 1rem;
  font-family: var(--font-display);
  font-size: 2rem;
  color: var(--white);
`;

const BackBtn = styled.button`
  font-family: var(--font-condensed);
  font-size: 0.85rem;
  background: var(--accent);
  border: none;
  border-radius: 999px;
  padding: 0.8rem 2rem;
  color: var(--white);
  cursor: pointer;
`;

const Hero = styled.div`
  padding-top: 6rem;
  padding-bottom: 4rem;
  min-height: 90vh;
  display: flex;
  align-items: center;
`;

const HeroInner = styled.div`
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 4rem;
`;

const BackLink = styled.button`
  font-family: var(--font-condensed);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--white-dim);
  background: none;
  border: none;
  cursor: pointer;
  margin-bottom: 3rem;
  display: block;
  opacity: 0;
  transition: color 0.2s;
  &:hover {
    color: var(--accent);
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ProductVisual = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 520px;
  opacity: 0;
`;

const VisualGlow = styled.div`
  position: absolute;
  width: 380px;
  height: 380px;
  border-radius: 50%;
  filter: blur(80px);
  animation: ${pulse} 4s ease-in-out infinite;
`;

const ProductHeroImg = styled.img`
  width: 100%;
  max-width: 420px;
  height: 480px;
  object-fit: cover;
  border-radius: 6px;
  position: relative;
  z-index: 1;
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.6);
  transition: transform 0.4s ease;
  &:hover {
    transform: scale(1.02);
  }
`;

const YearTag = styled.div`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  font-family: var(--font-display);
  font-size: 4rem;
  color: rgba(255, 255, 255, 0.04);
  pointer-events: none;
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const TypeBadge = styled.span`
  display: inline-block;
  font-family: var(--font-condensed);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--accent);
  border: 1px solid rgba(139, 58, 26, 0.3);
  border-radius: 999px;
  padding: 0.3rem 0.9rem;
  width: fit-content;
`;

const ProductTitle = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(2.5rem, 5vw, 4rem);
  line-height: 0.95;
  color: var(--white);
  letter-spacing: -0.01em;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const BrandName = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: var(--white-dim);
  text-transform: uppercase;
`;

const OriginTag = styled.span`
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: rgba(240, 236, 230, 0.4);
  font-style: italic;
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const Stars = styled.span`
  font-size: 0.9rem;
  color: var(--accent);
  letter-spacing: 2px;
`;

const RatingNum = styled.span`
  font-family: var(--font-display);
  font-size: 1.1rem;
  color: var(--white);
`;

const ReviewText = styled.span`
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: var(--white-dim);
`;

const Description = styled.p`
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.8;
  color: var(--white-dim);
  border-left: 2px solid var(--accent);
  padding-left: 1.2rem;
`;

const SpecsGrid = styled.div`
  display: flex;
  gap: 2rem;
`;

const Spec = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const SpecLabel = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(240, 236, 230, 0.3);
`;

const SpecVal = styled.span`
  font-family: var(--font-display);
  font-size: 1.1rem;
  color: var(--white);
`;

const NotesBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const NotesTitle = styled.p`
  font-family: var(--font-condensed);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(240, 236, 230, 0.4);
`;

const NotesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const NoteTag = styled.span`
  font-family: var(--font-body);
  font-size: 0.78rem;
  color: var(--white);
  background: rgba(139, 58, 26, 0.12);
  border: 1px solid rgba(139, 58, 26, 0.2);
  border-radius: 999px;
  padding: 0.3rem 0.9rem;
`;

const FoodTag = styled(NoteTag)`
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
`;

const PurchaseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(139, 58, 26, 0.15);
  flex-wrap: wrap;
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 0.5rem;
`;

const BigPrice = styled.span`
  font-family: var(--font-display);
  font-size: 2.5rem;
  color: var(--white);
  line-height: 1;
`;

const PriceSub = styled.span`
  font-family: var(--font-body);
  font-size: 0.72rem;
  color: var(--white-dim);
  margin-top: 2px;
`;

const QtyBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  border: 1px solid rgba(139, 58, 26, 0.25);
  border-radius: 999px;
  padding: 0.5rem 1rem;
`;

const QtyBtn = styled.button`
  background: none;
  border: none;
  color: var(--white);
  font-size: 1.2rem;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  &:hover {
    color: var(--accent);
  }
`;

const QtyNum = styled.span`
  font-family: var(--font-display);
  font-size: 1.2rem;
  color: var(--white);
  min-width: 28px;
  text-align: center;
`;

const AddToCartBtn = styled.button`
  flex: 1;
  padding: 1rem 1.5rem;
  background: ${({ $added }) =>
    $added ? "rgba(139,58,26,0.3)" : "transparent"};
  border: 1.5px solid var(--accent);
  border-radius: 999px;
  font-family: var(--font-condensed);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--white);
  cursor: pointer;
  transition: all 0.25s ease;
  &:hover {
    background: rgba(139, 58, 26, 0.2);
    transform: scale(1.02);
  }
`;

const BuyNowBtn = styled.button`
  flex: 1;
  padding: 1rem 1.5rem;
  background: var(--accent);
  border: none;
  border-radius: 999px;
  font-family: var(--font-condensed);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--white);
  cursor: pointer;
  transition: all 0.25s ease;
  &:hover {
    background: var(--accent-light);
    transform: scale(1.02);
  }
`;

/* Related */
const RelatedSection = styled.div`
  padding: 4rem 0 6rem;
  border-top: 1px solid rgba(139, 58, 26, 0.12);
`;

const RelatedInner = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 4rem;
`;

const RelatedTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 2rem;
  color: var(--white);
  margin-bottom: 2rem;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
`;

const RelatedCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(139, 58, 26, 0.12);
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  opacity: 0;
  &:hover {
    border-color: rgba(139, 58, 26, 0.4);
    transform: translateY(-4px);
  }
`;

const RelatedImg = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  transition: transform 0.4s ease;
  ${RelatedCard}:hover & {
    transform: scale(1.05);
  }
`;

const RelatedBody = styled.div`
  padding: 1rem 1.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RelatedName = styled.p`
  font-family: var(--font-condensed);
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--white);
`;

const RelatedPrice = styled.p`
  font-family: var(--font-display);
  font-size: 1.1rem;
  color: var(--accent);
`;
